/**
 * SecurityDesk sub-components and helpers — extracted to keep the main
 * component file under the 400-line limit.
 */

import { useMemo, useState } from "react";
import type { ConflictEventType, SecurityNewsItem, SecurityTimeline } from "@yala/shared";

// ── time formatting ────────────────────────────────────────────────────────

export function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}

// ── event type maps ────────────────────────────────────────────────────────

export const EVENT_BADGE: Record<ConflictEventType, string> = {
  "bombing-ied": "BOMB",
  shooting: "SHOOT",
  "armed-clash": "CLASH",
  arson: "ARSON",
  "raid-arrest": "ARREST",
  abduction: "ABDUCT",
  "remote-violence": "REMOTE",
  other: "SEC",
};

export const EVENT_COLOR: Record<ConflictEventType, string> = {
  "bombing-ied": "var(--bad)",
  shooting: "var(--bad)",
  "armed-clash": "var(--bad)",
  arson: "var(--warn)",
  "raid-arrest": "var(--warn)",
  abduction: "var(--warn)",
  "remote-violence": "var(--bad)",
  other: "var(--ink-mid)",
};

export const SEVERITY_LABEL: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: "MONITORING",
  2: "LOW",
  3: "ELEVATED",
  4: "HIGH",
  5: "CRITICAL",
};

export function severityColor(level: 1 | 2 | 3 | 4 | 5): string {
  if (level <= 2) return "var(--ok)";
  if (level === 3) return "var(--warn)";
  return "var(--bad)";
}

export function trendArrow(trend: "rising" | "stable" | "falling"): string {
  if (trend === "rising") return "▲";
  if (trend === "falling") return "▼";
  return "◆";
}

export function trendColor(trend: "rising" | "stable" | "falling"): string {
  if (trend === "rising") return "var(--bad)";
  if (trend === "falling") return "var(--ok)";
  return "var(--warn)";
}

// ── TimelineChart ──────────────────────────────────────────────────────────

// Chart geometry (SVG user units). viewBox is 360 × 72; the plot area leaves a
// 1px floor for the baseline and the bottom labels sit just under it.
const VB_W = 360;
const VB_H = 72;
const BASELINE_Y = 64; // bars + overlays sit above this; tick labels below
const PLOT_H = 56; // vertical room for the tallest bar / line

const MA_WINDOW = 7; // trailing moving-average window (days)
const BASELINE_WINDOW = 30; // prior-period window for the reference line

/** Worst event type present on a given day, mapped to its token colour. */
function worstTypeColor(byType: Partial<Record<ConflictEventType, number>>): string {
  // Order = severity priority; first match wins.
  const priority: ConflictEventType[] = [
    "bombing-ied",
    "remote-violence",
    "armed-clash",
    "shooting",
    "abduction",
    "arson",
    "raid-arrest",
    "other",
  ];
  for (const t of priority) {
    if ((byType[t] ?? 0) > 0) return EVENT_COLOR[t];
  }
  return "var(--ink-low)";
}

/** Trailing simple moving average of daily counts over `window` days. */
function trailingMovingAverage(counts: readonly number[], window: number): number[] {
  return counts.map((_, i) => {
    const start = Math.max(0, i - window + 1);
    const slice = counts.slice(start, i + 1);
    const sum = slice.reduce((a, b) => a + b, 0);
    return sum / slice.length;
  });
}

/** Compact per-type breakdown string for a day, e.g. "BOMB 2 · SHOOT 1". */
function dayBreakdown(byType: Partial<Record<ConflictEventType, number>>): string {
  const parts = (Object.entries(byType) as [ConflictEventType, number][])
    .filter(([, n]) => n > 0)
    .sort(([, a], [, b]) => b - a)
    .map(([t, n]) => `${EVENT_BADGE[t]} ${n}`);
  return parts.length > 0 ? parts.join(" · ") : "no incidents";
}

interface TimelineChartProps {
  days: SecurityTimeline["days"];
  /** Plot height in CSS pixels. Optional — defaults to a legible 64px. */
  height?: number;
}

export function TimelineChart({ days, height = 64 }: TimelineChartProps) {
  const [hovered, setHovered] = useState<number | null>(null);

  const maxCount = useMemo(() => Math.max(...days.map((d) => d.count), 1), [days]);
  const barW = VB_W / Math.max(days.length, 1);

  // 7-day trailing moving average → polyline points (centred on each bar).
  const movingAvg = useMemo(
    () => trailingMovingAverage(days.map((d) => d.count), MA_WINDOW),
    [days],
  );
  const yFor = (v: number) => BASELINE_Y - (v / maxCount) * PLOT_H;
  const maLine = useMemo(
    () => movingAvg.map((v, i) => `${(i * barW + barW / 2).toFixed(2)},${yFor(v).toFixed(2)}`).join(" "),
    [movingAvg, barW, maxCount],
  );

  // Prior-30-day mean daily count → dashed reference line. Prior window is the
  // 30 days *before* the most recent 30, matching the trendPct definition.
  const priorMean = useMemo(() => {
    const n = days.length;
    const priorStart = Math.max(0, n - 2 * BASELINE_WINDOW);
    const priorEnd = Math.max(0, n - BASELINE_WINDOW);
    const slice = days.slice(priorStart, priorEnd);
    if (slice.length === 0) return 0;
    return slice.reduce((s, d) => s + d.count, 0) / slice.length;
  }, [days]);
  const priorMeanY = yFor(priorMean);

  // Peak day → marker dot so the y-scale reads without an axis.
  const peakIndex = useMemo(() => {
    let idx = 0;
    for (let i = 1; i < days.length; i++) if (days[i].count > days[idx].count) idx = i;
    return days.length > 0 && days[idx].count > 0 ? idx : -1;
  }, [days]);

  const hday = hovered != null ? days[hovered] : null;
  // Tooltip horizontal anchor as a percentage so it tracks the hovered bar.
  const tipLeftPct = hovered != null ? ((hovered + 0.5) / days.length) * 100 : 0;

  return (
    <div style={{ position: "relative" }}>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height, display: "block" }}
        role="img"
        aria-label={`${days.length}-day incident timeline with 7-day moving average`}
        onMouseLeave={() => setHovered(null)}
      >
        {/* baseline */}
        <line x1="0" y1={BASELINE_Y} x2={VB_W} y2={BASELINE_Y} stroke="var(--rule)" strokeWidth="0.5" />

        {/* prior-30d mean reference line (hairline dashed) */}
        {priorMean > 0 && (
          <line
            x1="0"
            y1={priorMeanY}
            x2={VB_W}
            y2={priorMeanY}
            stroke="var(--line-2)"
            strokeWidth="0.75"
            strokeDasharray="3 3"
          />
        )}

        {/* daily bars, coloured by worst event type */}
        {days.map((day, i) => {
          const h = Math.max((day.count / maxCount) * PLOT_H, day.count > 0 ? 1.5 : 0);
          const isHovered = hovered === i;
          return (
            <rect
              key={day.date}
              x={i * barW + 0.5}
              y={BASELINE_Y - h}
              width={Math.max(barW - 0.8, 0.5)}
              height={h}
              fill={worstTypeColor(day.byType)}
              opacity={hovered == null ? 0.75 : isHovered ? 1 : 0.35}
            >
              <title>{`${day.date} — ${day.count} incident${day.count === 1 ? "" : "s"} · ${dayBreakdown(day.byType)}`}</title>
            </rect>
          );
        })}

        {/* 7-day moving-average overlay (Rams-clean polyline) */}
        <polyline
          points={maLine}
          fill="none"
          stroke="var(--ink)"
          strokeWidth="1.1"
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity={hovered == null ? 0.9 : 0.45}
          vectorEffect="non-scaling-stroke"
        />

        {/* peak-day marker */}
        {peakIndex >= 0 && (
          <circle
            cx={peakIndex * barW + barW / 2}
            cy={yFor(days[peakIndex].count)}
            r="1.6"
            fill="var(--bad)"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* transparent hover hit-targets (full-height, easier to hit than thin bars) */}
        {days.map((day, i) => (
          <rect
            key={`hit-${day.date}`}
            x={i * barW}
            y={0}
            width={barW}
            height={BASELINE_Y}
            fill="transparent"
            onMouseEnter={() => setHovered(i)}
          />
        ))}

        {/* hovered-bar guide line */}
        {hovered != null && (
          <line
            x1={hovered * barW + barW / 2}
            y1={0}
            x2={hovered * barW + barW / 2}
            y2={BASELINE_Y}
            stroke="var(--ink-low)"
            strokeWidth="0.5"
          />
        )}
      </svg>

      {/* y-scale hint (peak) + 30d-avg label — 11px uppercase letterspaced */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 2,
        }}
      >
        <span className="eyebrow mono" style={{ fontSize: "0.58rem", color: "var(--ink-low)" }}>
          PEAK {days[peakIndex >= 0 ? peakIndex : 0]?.count ?? 0}/DAY
        </span>
        {priorMean > 0 && (
          <span
            className="eyebrow mono"
            style={{ fontSize: "0.58rem", color: "var(--ink-low)", fontVariantNumeric: "tabular-nums" }}
          >
            30D AVG {priorMean.toFixed(1)}
          </span>
        )}
      </div>

      {/* hover tooltip — absolutely positioned, tracks the hovered bar */}
      {hday && (
        <div
          role="status"
          style={{
            position: "absolute",
            top: 0,
            left: `${tipLeftPct}%`,
            transform: `translateX(${tipLeftPct > 60 ? "-100%" : tipLeftPct < 40 ? "0" : "-50%"})`,
            pointerEvents: "none",
            background: "var(--panel)",
            border: "1px solid var(--line-2)",
            padding: "4px 6px",
            zIndex: 5,
            whiteSpace: "nowrap",
          }}
        >
          <div
            className="eyebrow mono"
            style={{ fontSize: "0.58rem", color: "var(--ink-mid)", fontVariantNumeric: "tabular-nums" }}
          >
            {hday.date}
          </div>
          <div
            className="mono"
            style={{ fontSize: "0.7rem", color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}
          >
            {hday.count} incident{hday.count === 1 ? "" : "s"}
            {hday.fatalities > 0 ? ` · ${hday.fatalities} fatal` : ""}
          </div>
          <div className="mono" style={{ fontSize: "0.6rem", color: "var(--ink-low)", marginTop: 1 }}>
            {dayBreakdown(hday.byType)}
          </div>
        </div>
      )}
    </div>
  );
}

// ── IntelFeedItem ──────────────────────────────────────────────────────────

interface IntelFeedItemProps {
  item: SecurityNewsItem;
  index: number;
}

export function IntelFeedItem({ item, index }: IntelFeedItemProps) {
  const badge = item.eventType ? EVENT_BADGE[item.eventType] : "SEC";
  const color = item.eventType ? EVENT_COLOR[item.eventType] : "var(--ink-mid)";
  const sevColor = severityColor(item.severity);
  const province = item.province?.toUpperCase() ?? "REGIONAL";

  return (
    <a
      href={item.sourceUrl}
      target="_blank"
      rel="noreferrer"
      style={{ display: "block", marginTop: 6, textDecoration: "none", color: "inherit" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        <span className="mono" style={{ fontSize: "0.6rem", color: "var(--ink-low)" }}>
          [{String(index + 1).padStart(3, "0")}]
        </span>
        <span
          className="eyebrow mono"
          style={{
            fontSize: "0.6rem",
            color,
            border: `1px solid ${color}`,
            padding: "0 3px",
            borderRadius: 2,
          }}
        >
          {badge}
        </span>
        <span
          className="eyebrow mono"
          style={{
            fontSize: "0.6rem",
            color: "var(--ink-mid)",
            border: "1px solid var(--rule)",
            padding: "0 3px",
            borderRadius: 2,
          }}
        >
          {province}
        </span>
        <span style={{ color: sevColor, fontSize: "0.65rem" }} title={`Severity ${item.severity}`}>
          ●
        </span>
        <span className="mono" style={{ fontSize: "0.6rem", color: "var(--ink-low)", marginLeft: "auto" }}>
          {timeAgo(item.publishedAt)}
        </span>
      </div>
      <div style={{ fontSize: "0.7rem", lineHeight: 1.3, marginTop: 2, color: "var(--ink)" }}>
        {item.title}
      </div>
      <div className="mono" style={{ fontSize: "0.6rem", color: "var(--ink-low)", marginTop: 1 }}>
        {item.source.toUpperCase()}
        {item.placeName ? ` · ${item.placeName}` : ""}
      </div>
    </a>
  );
}

// ── BreakdownBar ───────────────────────────────────────────────────────────

interface BreakdownBarProps {
  label: string;
  count: number;
  max: number;
  color?: string;
}

export function BreakdownBar({ label, count, max, color = "var(--bad)" }: BreakdownBarProps) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div style={{ marginTop: 3 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 1 }}>
        <span className="eyebrow mono" style={{ fontSize: "0.58rem", minWidth: 70 }}>
          {label}
        </span>
        <div
          style={{
            height: 4,
            width: `${pct}%`,
            minWidth: count > 0 ? 2 : 0,
            background: color,
            borderRadius: 2,
            flex: "0 0 auto",
            maxWidth: 80,
          }}
        />
        <span className="mono" style={{ fontSize: "0.6rem", color: "var(--ink-low)" }}>{count}</span>
      </div>
    </div>
  );
}
